import axios, { type AxiosError, type AxiosResponse } from 'axios'

import type { SecondaryFactor } from '../../types'
import { logEvent } from '../datadog'
import { environmentVariables } from '../../utils/environmentVariables'
import {
  ErrorCodes,
  FailedAuthenticationError,
  InvalidPasswordForPasswordChangeError,
  MfaRequiredError,
  TooManyAttemptsError,
  UserFacingError,
  AccountDeletionCancellationRequiredError,
} from '../../utils/errors'
import { redirectToUrl } from '../../utils/location'
import type { Platform } from '../../routes/BaseSignInRoute'

type AuthenticationTokens = {
  access_token: string
  refresh_token: string
}

type AuthenticationUrl = {
  redirect_to: string
}

type DirectoryApiError = {
  error?: string
  error_code?: string
}

type VerifiedCredentialsUserEmail = {
  email: string
}

type UserIdentity = {
  sub: string
  email: string
  phone_number?: string
  title?: string
  nickname?: string
  job_title?: string
  given_name?: string
  family_name?: string
  birthdate?: string
  locale?: string
  country_code?: string
  is_onboarded: boolean
  is_fgtafo: boolean
}

export interface TwoStepVerificationCodeApiError extends DirectoryApiError {
  recipient: string
  channel: SecondaryFactor
}

type TwoStepVerificationDetails = {
  channel: string
  recipient: string
  verification_code?: string
}

export const directoryApiClient = axios.create({
  baseURL: environmentVariables('AUTH_API_URL'),
  headers: {
    'X-Swile-Platform': 'web',
  },
})

/**
 * Updates the X-Swile-Platform header for all future API requests
 * @param platform The platform making the request (web, android, ios, partner)
 */
export const updatePlatformHeader = (platform: Platform): void => {
  directoryApiClient.defaults.headers.common['X-Swile-Platform'] = platform
  directoryApiClient.defaults.headers['X-Swile-Platform'] = platform
}

export const isSso = async (email: string): Promise<boolean> => {
  try {
    const response = await directoryApiClient.get('/sso/info', { params: { email } })
    return response.data
  } catch {
    return false
  }
}

export const ssoInit = (email: string, redirectUri: string): void => {
  const redirectUrl = new URL(`${environmentVariables('AUTH_API_URL')}/sso/init`)
  redirectUrl.searchParams.append('email', email)
  redirectUrl.searchParams.append('redirect_uri', redirectUri)
  redirectToUrl(redirectUrl)
}

export const signInPassword = async (
  username: string,
  password: string,
  authenticationCode?: string,
  secondaryFactor?: string,
  cancelDeletion?: boolean,
): Promise<AuthenticationTokens> => {
  return directoryApiClient
    .post<AuthenticationTokens>('/oauth/token', {
      client_id: environmentVariables('AUTH_CLIENT_ID'),
      grant_type: 'password',
      username,
      password,
      authentication_code: authenticationCode,
      secondary_factor: secondaryFactor,
      cancelDeletion: cancelDeletion,
    })
    .then((response) => {
      const verifiedIdentity = response.headers['verified-identity']
      const verifiedCredentials = response.headers['verified-credentials']
      if (verifiedIdentity) {
        directoryApiClient.defaults.headers.common['verified-identity'] = verifiedIdentity
      }
      if (verifiedCredentials) {
        directoryApiClient.defaults.headers.common['verified-credentials'] = verifiedCredentials
      }

      logEvent('password_login_success', username)
      return response.data
    })
    .catch((error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          throw new TooManyAttemptsError()
        }
        logEvent(
          'password_login_error',
          username,
          {
            error_status: error.response?.status,
            error_message: error.response?.data?.error,
          },
          error,
          'error',
        )
        handleSignInError(error)
      }
      throw new Error('Error during POST oauth/token { grant_type: "password" }', { cause: error })
    })
}

export const appendVerifiedParamsToUrl = (redirectTo: string): string => {
  const redirectUrl = new URL(redirectTo, window.location.origin)

  const verifiedIdentity = directoryApiClient.defaults.headers.common['verified-identity']

  if (verifiedIdentity) {
    redirectUrl.searchParams.append('verified-identity', verifiedIdentity as string)
  }

  return redirectUrl.pathname + redirectUrl.search
}

export const signInAuthorizationCodeWithToken = async (
  token: string,
  redirectTo: string,
  authenticationCode?: string,
  secondaryFactor?: string,
): Promise<AuthenticationUrl> => {
  const redirectUrl = appendVerifiedParamsToUrl(redirectTo)
  return directoryApiClient
    .post<AuthenticationUrl>(
      '/session',
      {
        redirect_to: redirectUrl,
        remember_me: '1',
        authentication_code: authenticationCode,
        secondary_factor: secondaryFactor,
      },
      {
        headers: {
          'access-token': `${token}`,
          'verified-credentials': directoryApiClient.defaults.headers.common['verified-credentials'],
          'verified-identity': directoryApiClient.defaults.headers.common['verified-identity'],
        },
      },
    )
    .then((response) => response.data)
    .catch((error) => {
      if (axios.isAxiosError(error)) {
        handleSignInError(error)
      }
      throw new Error('Error during POST session with token { grant_type: "authorization_code" }', { cause: error })
    })
}

const handleSignInError = (error: AxiosError<DirectoryApiError>): void => {
  if (error.response?.data.error === ErrorCodes.INVALID_GRANT) {
    throw new FailedAuthenticationError()
  }
  if (error.response?.data.error === ErrorCodes.MISSING_AUTHENTICATION_CODE) {
    const { recipient, channel } = error.response.data as TwoStepVerificationCodeApiError
    throw new MfaRequiredError(channel, recipient)
  }
  if (error.response?.data.error === ErrorCodes.ACCOUNT_DELETION_CANCELLATION_REQUIRED) {
    const token = (error.response.data as any).token
    const request_uuid = (error.response.data as any).request_uuid
    throw new AccountDeletionCancellationRequiredError(token, request_uuid)
  }
}

export const exchangeToken = async (refreshToken: string): Promise<AuthenticationTokens> => {
  return directoryApiClient
    .post('/oauth/token', {
      client_id: environmentVariables('AUTH_CLIENT_ID'),
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    })
    .then((response: AxiosResponse<AuthenticationTokens>) => response.data)
    .catch((error) => {
      throw new Error('Error during POST oauth/token { grant_type: "refresh_token" }', { cause: error })
    })
}

export const getVerifiedCredentialsUserEmail = async (): Promise<VerifiedCredentialsUserEmail> => {
  const { data } = await directoryApiClient.get('/oauth/accounts/email')
  return data
}

export const getUserIdentity = async (
  uuid: string,
  accessToken: string,
  { timeout }: { timeout?: number } = {},
): Promise<UserIdentity> => {
  const { data } = await directoryApiClient.get(`/identities/${uuid}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    timeout,
  })
  return data.identity
}

export const updateCurrentlyVerifiedUserEmail = async (
  email: string,
  twoStepVerificationCode: string,
): Promise<VerifiedCredentialsUserEmail> => {
  return directoryApiClient
    .put(
      '/oauth/accounts/email',
      { email },
      { headers: { 'X-Swile-Two-Step-Verification-Code': twoStepVerificationCode } },
    )
    .then((response: AxiosResponse) => response.data)
    .catch((error) => {
      if (error.response.status === 422) {
        throw new UserFacingError('global.error_title', 'change_email.update_email_error')
      }
      if (
        error.response.status === 403 &&
        error.response.data.error.message === ErrorCodes.INVALID_TWO_STEP_VERIFICATION_CODE
      ) {
        const { recipient, channel } = error.response.data as TwoStepVerificationCodeApiError
        throw new MfaRequiredError(channel, recipient)
      }
      throw error
    })
}

export const postTwoStepVerificationCodeToCurrentlyVerifiedUser = async (): Promise<TwoStepVerificationDetails> => {
  return directoryApiClient
    .post('/two_step_verification/code', { channel: 'sms', operation: 'loginemailupdate' })
    .then((response: AxiosResponse<TwoStepVerificationDetails>) => response.data)
    .catch((error) => {
      if (error.response.status === 401) {
        throw new UserFacingError('global.error_title', 'change_email.error_expired_session_error')
      }
      throw error
    })
}

export const processResetPassword = ({
  newPassword,
  newPasswordConfirmation,
  jwt,
}: { newPassword: string; newPasswordConfirmation: string; jwt: string }) =>
  directoryApiClient
    .post(
      'reset_password/update',
      {
        password: newPassword,
        password_confirmation: newPasswordConfirmation,
      },
      {
        headers: { authorization: `Bearer ${jwt}` },
      },
    )
    .catch((error) => {
      if (axios.isAxiosError(error)) {
        if (error?.response?.data?.error?.code === 'invalid_password') {
          throw new InvalidPasswordForPasswordChangeError(error.response.data.code)
        }
      }
      throw error
    })

/**
 * Cancels a PII deletion request
 * @param request_uuid The UUID of the request to cancel
 * @param token Optional token for authorization
 */
export const cancelPiiDeletionRequest = async (request_uuid: string, token: string): Promise<void> => {
  try {
    const headers: Record<string, string> = {}
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    await directoryApiClient.delete(`/pii_deletion_requests/${request_uuid}`, { headers })
  } catch (error) {
    throw new Error('Error during DELETE pii_deletion_requests/:id', { cause: error })
  }
}

/**
 * Verifies user credentials without generating a token
 * Used during two-step verification to add an extra layer of security
 * @param username The username (email) to verify
 * @param password The password to verify
 * @returns A promise that resolves if verification is successful, rejects otherwise
 */
export const fetchAvailableChannels = async (accessToken: string): Promise<string[]> => {
  const { data } = await directoryApiClient.get<{ channels: string[] }>(
    '/api/auth/two_step_verification/available_channels',
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  return data.channels ?? []
}

export const verifyCredentials = async (username: string, password: string): Promise<void> => {
  try {
    await directoryApiClient.post('/api/auth/credentials/verify', {
      username,
      password,
    })
    logEvent('credentials_verification_success', username)
    return Promise.resolve()
  } catch (error) {
    logEvent('credentials_verification_error', username, {}, error as Error, 'error')
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      throw new FailedAuthenticationError()
    }
    throw new Error('Error during credentials verification', { cause: error })
  }
}
